import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import { playNewMessageNotificationInWidget } from 'widget/helpers/WidgetAudioNotificationHelper';
import { ON_AGENT_MESSAGE_RECEIVED } from '../constants/widgetBusEvents';
import { IFrameHelper } from 'widget/helpers/utils';
import { LocalStorage } from '../../shared/helpers/localStorage';
import { getLastConversationLSKey } from './urlParamsHelper';

const hasShowConversationHistoryEnabled = window.hooryWebChannel.enabledFeatures.includes(
  'show_conversation_history'
);

const isMessageInConversationList = message => {
  const { conversation_id: conversationId } = message;
  const activeConversationId = window?.hooryCurrentConversationDid || '';

  return (
    hasShowConversationHistoryEnabled &&
    (!activeConversationId || conversationId !== activeConversationId)
  );
};

const getContactInitialMessageConvId = conversations => {
  return Object.values(conversations)?.find(
    item =>
      item.id &&
      item.conversation_id &&
      !item?.content_attributes?.is_greeting_message
  )?.conversation_id;
};

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    super(app, pubsubToken);
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.status_changed': this.onStatusChange,
      'conversation.created': this.onConversationCreated,
      'conversation.updated': this.onConversationUpdated,
      'presence.update': this.onPresenceUpdate,
      'contact.merged': this.onContactMerge,
    };
  }

  forceRouteReplace = (params = {}) => {
    // No need to force route replace if the route is already different
    if (this.app.$route.name !== 'messages') {
      return this.app.$router.replace({ name: 'messages', params }).catch();
    }
    // Not switching to messages if the conversation is already open
    if (!window.hooryCurrentConversation) {
      return new Promise(resolve => resolve());
    }
    return this.app.$router.replace({ name: 'redirect' }).then(() => {
      setTimeout(() => {
        return this.app.$router.replace({ name: 'messages', params }).catch();
      }, 200);
    });
  };

  onDisconnected = () => {
    this.setLastMessageId();
  };

  onReconnect = () => {
    window.logger.logUserAction('create-message-info', 'Socket re-connected');
    setTimeout(() => {
      this.syncLatestMessages();
    }, 0);
  };

  setLastMessageId = () => {
    this.app.$store.dispatch('conversation/setLastMessageId');
  };

  syncLatestMessages = () => {
    this.app.$store.dispatch('conversation/syncLatestMessages');
  };

  onStatusChange = data => {
    window.logger.logUserAction('info', 'Socket status changed', { data });

    if (data.status === 'resolved') {
      this.app.$store.dispatch('campaign/resetCampaign');
    }

    if (
      !isMessageInConversationList({ ...data, conversation_id: data?.id })
      // TODO: Enable this if we decide to show resolved conversations when history is disabled
      // (hasShowConversationHistoryEnabled ||
      //   (!hasShowConversationHistoryEnabled && data.status === 'resolved'))
    ) {
      this.app.$store.dispatch('conversationAttributes/set', {
        id: data.real_id,
        did: data.id,
        status: data.status,
        in_queue: data.in_queue,
        archived: data.archived,
        customAttributes: data.custom_attributes || null,
      });
    }

    /**
     * Update conversation status on the list
     */
    if (isMessageInConversationList(data)) {
      this.app.$store.dispatch('conversation/setMessageStatusOnConversation', {
        conversationId: data.id,
        status: data.status,
      });
    }
  };

  static checkActiveConversation(id, loggerData = {}, initialConvId) {
    window.logger.logUserAction(
      'create-message-info',
      'Active conversation check',
      loggerData
    );
    const activeConversationVuexId =
      initialConvId || window?.hooryCurrentConversationDid || '';
    if (id !== activeConversationVuexId) return false;

    if (!hasShowConversationHistoryEnabled) {
      const activeConversationLocalStorageId = LocalStorage.get(
        getLastConversationLSKey()
      );

      return (
        id === activeConversationLocalStorageId?.did &&
        activeConversationLocalStorageId?.status === 'open'
      );
    }

    return true;
  }

  onMessageCreated = data => {
    if (data.content_attributes.is_greeting_message) return; // skip greeting

    const isWidgetOpen = this.app.$store.getters['appConfig/getIsWidgetOpen'];
    const conversations =
      this.app.$store?.state?.conversation?.conversations || {};
    const initialConvId = getContactInitialMessageConvId(conversations);

    const loggerData = {
      isAgentMessage: data.sender_type !== 'Contact',
      accountId: data?.account_id,
      messageId: data?.id,
      content: data?.content,
      createdAt: data?.id,
      conversationId: data?.conversation_id,
      inboxId: data?.inbox_id,
      activeConversationVuexId: window?.hooryCurrentConversationDid || '',
      activeConversationLocalStorageId: LocalStorage.get(
        getLastConversationLSKey()
      ),
      messageType: data?.message_type,
      status: data?.status,
      senderId: data?.sender?.id,
      senderName: data?.sender?.name,
      isWidgetOpen,
      initialConvId,
      conversations,
    };

    window.logger.logUserAction(
      'create-message-info',
      'Socket message received',
      loggerData
    );

    /**
     * if it is user message update the conversation attributes
     */
    if (data.sender_type === 'Contact' && hasShowConversationHistoryEnabled) {
      window.logger.logUserAction('info', 'User message saved in local');
      this.app.$store.dispatch('conversationAttributes/setLocalStorage', {
        id: data.conversation.id,
        did: data.conversation.display_id,
        status: data.conversation.status,
        in_queue: data.conversation.in_queue,
        archived: data.conversation.archived,
        customAttributes: data.conversation?.custom_attributes || null,
      });
    }

    /**
     * Check if message is not in current conversation save it in the list
     */
    if (
      (!isWidgetOpen || isMessageInConversationList(data)) &&
      !ActionCableConnector.checkActiveConversation(
        data.conversation_id,
        loggerData,
        initialConvId
      )
    ) {
      window.logger.logUserAction(
        'create-message-info',
        'Message was in different conv',
        loggerData
      );

      this.app.$store
        .dispatch('conversation/setLastMessageOnConversation', {
          conversationId: data.conversation_id,
          message: data,
        })
        .then(() => window.bus.$emit(ON_AGENT_MESSAGE_RECEIVED, data));

      IFrameHelper.sendMessage({ event: 'onMessage', data });
      if (data.sender_type === 'User') {
        playNewMessageNotificationInWidget();
      }
      return;
    }

    /**
     * Not show greeting message twice
     */
    if (
      window.hooryWebChannel.greetingEnabled &&
      data.content &&
      data.content
        .replace(/'/g, '&#39;')
        .replace(/"/g, '&quot;')
        .replace(/\\\r\n/g, '\n')
        .replace(/\r/g, '\n') === window.hooryWebChannel.greetingMessage
    ) {
      window.logger.logUserAction('info', 'Message was greeting');
      return;
    }

    data.isWidgetOpen = isWidgetOpen;
    this.app.$store
      .dispatch('conversation/addOrUpdateMessage', data)
      .then(() => window.bus.$emit(ON_AGENT_MESSAGE_RECEIVED, data));

    this.app.$store.dispatch('conversationAttributes/updateQueueStatus', {
      in_queue: data.conversation.in_queue,
    });

    IFrameHelper.sendMessage({ event: 'onMessage', data });
    // TODO: we have sound play when user in not in conversation or widget is closed
    // if (data.sender_type === 'User' && ) {
    //   playNewMessageNotificationInWidget();
    // }
  };

  onMessageUpdated = data => {
    window.logger.logUserAction('info', 'Socket message updated', { data });

    if (isMessageInConversationList(data)) {
      return;
    }
    this.app.$store.dispatch('conversation/addOrUpdateMessage', data);
  };

  onConversationCreated = async data => {
    window.logger.logUserAction('info', 'Socket conversation created', {
      data,
    });

    /**
     * If auto open unread conversation is enabled, open the conversation
     */
    const autoOpenUnreadConversation = await this.app.$store.getters[
      'appConfig/getAutoOpenUnreadConversation'
    ];
    const setConversationAttrs = () => {
      this.app.$store.dispatch('conversationAttributes/set', {
        id: data.real_id,
        did: data.id,
        status: data.status,
        in_queue: data.in_queue,
        archived: data.archived,
        customAttributes: data.custom_attributes || null,
      });
    };

    if (autoOpenUnreadConversation) {
      this.forceRouteReplace({
        conversation: data.real_id,
        conversationDid: data.id,
        conversationStatus: data.status,
        in_queue: data.in_queue,
        archived: data.archived,
        customAttributes: data.custom_attributes || null,
      }).then(() => {
        setConversationAttrs();
      });
    } else if (!window.hooryCurrentConversation) {
      setConversationAttrs();
    }
  };

  onConversationUpdated = data => {
    const conversationParams = this.app.$store.getters[
      'conversationAttributes/getConversationParams'
    ];
    if (conversationParams.did === data.id) {
      this.app.$store.dispatch('conversationAttributes/updateQueueStatus', {
        in_queue: data.in_queue,
      });
      this.app.$store.dispatch('conversationAttributes/updateArchivedStatus', {
        archived: data.archived,
      });
      this.app.$store.dispatch(
        'conversationAttributes/updateCustomAttributes',
        {
          customAttributes: data.custom_attributes,
        }
      );
    }

    if (data && data.status === 'resolved') {
      IFrameHelper.sendMessage({ event: 'onConversationResolved', data });
    }
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('agent/updatePresence', data.users);
  };

  onContactMerge = data => {
    const { pubsub_token: pubsubToken } = data;
    ActionCableConnector.refreshConnector(pubsubToken);
  };

  onTypingOn = data => {
    const conversations =
      this.app.$store?.state?.conversation?.conversations || {};
    const activeConversationId = getContactInitialMessageConvId(conversations);

    const isUserTypingOnAnotherConversation =
      hasShowConversationHistoryEnabled &&
      data.conversation &&
      data.conversation.id !== activeConversationId;

    if (data.is_private) return;
    if (isUserTypingOnAnotherConversation) {
      this.app.$store.dispatch('conversation/toggleAgentTypingList', {
        status: 'on',
        conversationId: data.conversation.id,
      });
      return;
    }
    this.clearTimer();
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'on',
    });
    this.initTimer();
  };

  onTypingOff = data => {
    this.clearTimer();
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'off',
    });

    if (data?.conversation?.id) {
      this.app.$store.dispatch('conversation/toggleAgentTypingList', {
        status: 'off',
        conversationId: data.conversation.id,
      });
    }
  };

  clearTimer = () => {
    if (this.CancelTyping) {
      clearTimeout(this.CancelTyping);
      this.CancelTyping = null;
    }
  };

  initTimer = () => {
    // Turn off typing automatically after 30 seconds
    this.CancelTyping = setTimeout(() => {
      this.onTypingOff();
    }, 30000);
  };
}

export default ActionCableConnector;
